<template lang="pug">
div
  //- Loading State
  template(v-if="isFetching")
    yz-card.mb-4(v-for="n in 3" :key="n" outlined)
      .pa-4
        yz-skeleton-loader(type="list-item-avatar-two-line")

  //- Error State
  template(v-else-if="isError")
    yz-empty-state-card(icon="$yzi-alert-circle" icon-color="yz-danger-500" title="Unable to load branches" description="Please try again" action-label="Retry" @action-click="fetchBranches")

  //- Empty State (no branches at all)
  template(v-else-if="branches.length === 0 && !hasActiveFilters")
    yz-card.pa-8(outlined)
      .text-center.neutral500--text
        v-icon.mb-4(size="48" color="neutral400") $yzi-building-08
        .text-sm No branches found

  //- Empty State (filtered results empty)
  template(v-else-if="branches.length === 0")
    yz-card.pa-8(outlined)
      .text-center.neutral500--text
        v-icon.mb-4(size="48" color="neutral400") $yzi-search-lg
        .text-sm No branches match your filters

  //- Content
  template(v-else)
    yz-card.mb-4.cursor-pointer.hover-bg(v-for="branch in branches" :key="branch.id" outlined @click="onViewDetails(branch)")
      .d-flex.align-center.pa-4
        //- Location icon
        .mr-4
          yz-icon(size="30" color="neutral-500") $yzi-marker-pin-01

        //- Content section
        .flex-grow-1
          .font-semibold.text-sm.neutral700--text.mb-1 {{ branch.name }}
          .d-flex.align-center.text-sm.neutral500--text.flex-wrap
            template(v-if="branch.formattedAddress")
              span {{ branch.formattedAddress }}
            template(v-if="branch.email")
              yz-divider.mx-2(v-if="branch.formattedAddress" vertical)
              span {{ branch.email }}
            template(v-if="branch.phone")
              yz-divider.mx-2(v-if="branch.email" vertical)
              yz-btn(underline, :label="'+' + branch.phone" append-icon="mdi-whatsapp" icon-color="neutral-500" @click.stop="onOpenWhatsApp(branch.phone)")

        //- Actions
        .d-flex.align-center
          yz-menu(offset-y left)
            template(v-slot:activator="{ on, attrs }")
              yz-btn.mr-2(icon="$yzi-dots-horizontal" icon-only v-bind="attrs" v-on="on" @click.stop)
            yz-list
              yz-list-item(@click="onViewDetails(branch)")
                div View details
              template(v-if="canAddStorage(branch)")
                yz-divider
                yz-list-item(@click="onAddStorageLocation(branch)")
                  div Add storage location

          yz-btn(:icon="isExpanded(branch.id) ? '$yzi-chevron-up' : '$yzi-chevron-down'" icon-only @click.stop="onToggleExpand(branch.id)")

      //- Expanded content - Storage locations table
      template(v-if="isExpanded(branch.id)")
        .px-4.pb-4.table-scroll-wrapper
          yz-data-table(:headers="storageHeaders" :items="getStorageLocations(branch)" header-class="yz-medium-body-xs" body-class="yz-regular-body-sm")
            template(v-slot:item.name="{ item }")
              span.font-medium {{ item.name }}
            template(v-slot:item.createdDate="{ item }")
              span {{ item.createdDate }}
            template(v-slot:item.inventories="{ item }")
              span {{ item.inventories }}

  //- View Branch Details Dialog
  view-branch-details-dialog(v-if="showViewDetailsDialog" :open.sync="showViewDetailsDialog" :selected-branch="selectedBranchForView" :is-hq-mode="isHqMode" :current-company-slug="currentCompanySlug" @refresh="onStorageLocationSaved")

  //- Add Storage Location Dialog
  add-storage-location-dialog(v-if="showAddStorageDialog" :open.sync="showAddStorageDialog" :selected-branch="selectedBranchForStorage" @saved="onStorageLocationSaved" @deleted="onStorageLocationDeleted")
</template>

<script>
import { mapGetters } from 'vuex'
import ViewBranchDetailsDialog from './dialog/ViewBranchDetailsDialog.vue'
import AddStorageLocationDialog from './dialog/AddStorageLocationDialog.vue'

export default {
  components: {
    ViewBranchDetailsDialog,
    AddStorageLocationDialog
  },
  props: {
    filters: {
      type: Array,
      default: () => []
    },
    searchText: {
      type: String,
      default: ''
    },
    isHqMode: {
      type: Boolean,
      default: true
    },
    currentCompanySlug: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      expandedBranches: [],
      isFetching: true,
      isError: false,
      branches: [],
      totalBranches: 0,
      storageHeaders: [
        { text: 'Storage location', value: 'name', sortable: false },
        { text: 'Created date', value: 'createdDate', sortable: false },
        { text: 'Inventories', value: 'inventories', sortable: false }
      ],
      showViewDetailsDialog: false,
      selectedBranchForView: null,
      showAddStorageDialog: false,
      selectedBranchForStorage: null
    }
  },
  computed: {
    ...mapGetters({ company: 'getCompany' }),
    companyIdForListing() {
      // Use HQ company ID if this is a branch, otherwise use own company ID
      return this.company?.branch_of?.id || this.company?.id
    },
    hasActiveFilters() {
      return this.filters.length > 0 || this.searchText
    },
  },
  watch: {
    filters: {
      handler() {
        this.fetchBranches()
      },
      deep: true
    }
  },
  mounted() {
    this.fetchBranches()
  },
  methods: {
    isOwnBranch(branch) {
      return this.currentCompanySlug && branch.slug === this.currentCompanySlug
    },
    canEditBranch(branch) {
      // Only own branch can edit their branch details
      return this.isOwnBranch(branch)
    },
    canAddStorage(branch) {
      // HQ or own branch can add storage
      return this.isHqMode || this.isOwnBranch(branch)
    },
    async fetchBranches() {
      this.isFetching = true
      this.isError = false

      const params = {}

      // Add os param to filter clinic branches only
      params.os = 'clinic'

      // Add search param
      if (this.searchText) {
        params.search = this.searchText
      }

      // Add filter params from filters array
      this.filters.forEach(filter => {
        if (filter.key === 'date' && filter.value) {
          // Handle date filter
          const dates = Array.isArray(filter.value) ? filter.value : [filter.value, filter.value]
          params.created__gte = dates[0]
          params.created__lte = dates[1]
        } else if (filter.key && filter.value) {
          // Handle other filters (name, etc.)
          params[filter.key] = filter.value
        }
      })

      try {
        const response = await this.$axios.get(`/companies/${this.companyIdForListing}/branches`, { params })
        this.branches = response.data.results.map(branch => ({
          id: branch.id,
          slug: branch.slug,
          name: branch.name,
          email: branch.email || null,
          phone: branch.phone || null,
          address: branch.address,
          formattedAddress: this.formatAddress(branch.address),
          createdDate: branch.created ? new Date(branch.created) : null,
          storageLocations: (branch.storages || []).map(storage => ({
            id: storage.id,
            name: storage.value,
            value: storage.value,
            createdDate: storage.created ? this.formatDate(storage.created) : '-',
            inventories: storage.products?.length || 0,
            products: storage.products || []
          }))
        }))
        this.totalBranches = response.data.count
      } catch (error) {
        console.error('Error fetching branches:', error)
        this.isError = true
      } finally {
        this.isFetching = false
      }
    },
    formatAddress(address) {
      if (!address) return null
      const parts = [address.address_1, address.address_2, address.postcode, address.city, address.state].filter(Boolean)
      return parts.join(', ')
    },
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    },
    onOpenWhatsApp(phone) {
      const cleanPhone = phone.replace(/[^0-9]/g, '')
      window.open(`https://api.whatsapp.com/send?phone=${cleanPhone}&app_absent=1`)
    },
    onViewDetails(branch) {
      this.selectedBranchForView = branch
      this.showViewDetailsDialog = true
    },
    onAddStorageLocation(branch) {
      if (!this.canAddStorage(branch)) return
      this.selectedBranchForStorage = branch
      this.showAddStorageDialog = true
    },
    async onStorageLocationSaved() {
      // Refresh branches to get updated storage locations
      await this.fetchBranches()
      // Update selectedBranchForView with fresh data if dialog is open
      if (this.selectedBranchForView) {
        const updatedBranch = this.branches.find(b => b.id === this.selectedBranchForView.id)
        if (updatedBranch) {
          this.selectedBranchForView = updatedBranch
        }
      }
    },
    onStorageLocationDeleted() {
      // Refresh branches to get updated storage locations
      this.fetchBranches()
    },
    onToggleExpand(branchId) {
      const index = this.expandedBranches.indexOf(branchId)
      if (index > -1) {
        this.expandedBranches.splice(index, 1)
      } else {
        this.expandedBranches.push(branchId)
      }
    },
    isExpanded(branchId) {
      return this.expandedBranches.includes(branchId)
    },
    getStorageLocations(branch) {
      return branch.storageLocations || []
    }
  }
}
</script>

<style lang="scss" scoped>
.table-scroll-wrapper {
  overflow-x: auto;
}
.table-scroll-wrapper ::v-deep .yz-data-table {
  overflow: visible !important;
}
.table-scroll-wrapper ::v-deep .yz-data-table > .v-data-table__wrapper {
  overflow: visible !important;
}
.table-scroll-wrapper ::v-deep .yz-data-table > .v-data-table__wrapper > table {
  min-width: 1400px;
}
</style>
